import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ExternalLink, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import GoogleDocsPicker from '../../components/GoogleDocsPicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import '../../college-theme.css';

/* ─── Constants ──────────────────────────────────────────────────────── */

// Comma-separated Google Picker mime types so the Picker surfaces Docs,
// Sheets, Slides and PDFs (GoogleDocsPicker defaults to Docs-only).
const PICKER_MIME_TYPES = [
  'application/vnd.google-apps.document',
  'application/vnd.google-apps.spreadsheet',
  'application/vnd.google-apps.presentation',
  'application/pdf',
].join(',');

const MIME_TO_DOC_TYPE = {
  'application/vnd.google-apps.document': 'DOC',
  'application/vnd.google-apps.spreadsheet': 'SHEET',
  'application/vnd.google-apps.presentation': 'SLIDE',
  'application/pdf': 'PDF',
};

function docTypeFromMime(mimeType) {
  return MIME_TO_DOC_TYPE[mimeType] || 'OTHER';
}

const DOC_TYPE_META = {
  DOC: { letter: 'D', color: '#4285F4', label: 'Doc' },
  SHEET: { letter: 'S', color: '#0F9D58', label: 'Sheet' },
  SLIDE: { letter: 'G', color: '#F4B400', label: 'Slide' },
  PDF: { letter: 'P', color: '#C5221F', label: 'PDF' },
  OTHER: { letter: '?', color: '#8A8375', label: 'File' },
};

const DONE_GREEN = '#3E6B44';

// Preset quick-picks for category — free text is also allowed.
const CATEGORY_QUICK_PICKS = ['Essays', 'Recommendations & forms', 'Planning'];
const CATEGORY_ORDER = ['Essays', 'Recommendations & forms', 'Planning'];

// Radix Dialog portals its content to document.body, which sits OUTSIDE the
// .college-scope div — so var(--college-*) custom properties and the
// .font-serif-editorial / .font-mono-editorial utility classes do not resolve
// inside it. The dialog below uses literal hex values and font-family strings.
const HEX = {
  panel: '#FFFDF8', ink: '#16130D', inkSoft: '#4A453B',
  muted: '#7A7264', muted2: '#8A8375', border: '#DAD3C2',
};
const SERIF = "'Instrument Serif', Georgia, serif";
const MONO = "'IBM Plex Mono', 'Courier New', monospace";
const SANS = "'Instrument Sans', sans-serif";

const emptyConfirmForm = () => ({ name: '', url: '', docType: 'OTHER', category: '', collegeApplicationId: '' });

/* ─── Helpers ────────────────────────────────────────────────────────── */

function groupDocuments(docs) {
  const byCategory = {};
  docs.forEach((d) => {
    const key = d.category && d.category.trim() ? d.category.trim() : 'Other';
    (byCategory[key] = byCategory[key] || []).push(d);
  });
  const ordered = [];
  CATEGORY_ORDER.forEach((cat) => {
    if (byCategory[cat]?.length) {
      ordered.push({ title: cat, docs: byCategory[cat] });
      delete byCategory[cat];
    }
  });
  Object.keys(byCategory).forEach((cat) => {
    ordered.push({ title: cat, docs: byCategory[cat] });
  });
  return ordered;
}

/* ─── Row ────────────────────────────────────────────────────────────── */

function DocRow({ doc, appNameById, onToggleStatus, onDelete }) {
  const [hovered, setHovered] = useState(false);
  const meta = DOC_TYPE_META[doc.docType] || DOC_TYPE_META.OTHER;
  const isDone = doc.status === 'DONE';
  const linkedName = doc.collegeApplicationId ? appNameById[doc.collegeApplicationId] : null;
  const editedAt = doc.updatedAt ? new Date(doc.updatedAt) : null;
  const editedStr = editedAt && !Number.isNaN(editedAt.getTime()) ? format(editedAt, 'MMM d, yyyy') : '—';

  return (
    <div
      className="animate-fade-up"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 4px', borderBottom: '1px solid var(--college-border)' }}
    >
      <div
        className="font-mono-editorial"
        style={{
          width: 28, height: 28, flexShrink: 0, borderRadius: 5, background: meta.color, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
        }}
      >
        {meta.letter}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 15, fontWeight: 500, color: 'var(--college-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {doc.name}
        </p>
        <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--college-muted)' }}>
          {meta.label} - {doc.owner || 'me'}
        </p>
      </div>

      <span
        className="font-mono-editorial"
        style={{
          width: 150, flexShrink: 0, textAlign: 'right', fontSize: 11, color: 'var(--college-muted)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {linkedName || ''}
      </span>

      <span
        className="font-mono-editorial"
        style={{ width: 110, flexShrink: 0, textAlign: 'right', fontSize: 11, color: 'var(--college-muted-2)', whiteSpace: 'nowrap' }}
      >
        edited {editedStr}
      </span>

      <div style={{ width: 92, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <button
          type="button"
          onClick={() => onToggleStatus(doc)}
          className="font-mono-editorial"
          title="Click to toggle status"
          style={{
            fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
            padding: '4px 10px', borderRadius: 999, border: `1px solid ${isDone ? DONE_GREEN : 'var(--college-target)'}`,
            color: isDone ? DONE_GREEN : 'var(--college-target)', background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          {isDone ? 'Done' : 'Drafting'}
        </button>
      </div>

      <a
        href={doc.url}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          width: 64, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: 12,
          color: 'var(--college-ink)', textDecoration: 'none', whiteSpace: 'nowrap',
        }}
      >
        Open <ExternalLink size={12} />
      </a>

      <button
        type="button"
        onClick={() => onDelete(doc)}
        title="Remove link"
        style={{
          width: 20, flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          opacity: hovered ? 1 : 0, transition: 'opacity 0.15s', color: 'var(--college-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

/* ─── Section ────────────────────────────────────────────────────────── */

function DocSection({ title, docs, appNameById, onToggleStatus, onDelete }) {
  const doneCount = docs.filter((d) => d.status === 'DONE').length;
  return (
    <div style={{ marginBottom: 36 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4, padding: '0 4px' }}>
        <h2 className="font-serif-editorial" style={{ fontStyle: 'italic', fontWeight: 400, fontSize: 25, margin: 0 }}>
          {title}
        </h2>
        <p className="font-mono-editorial" style={{ margin: 0, fontSize: 12, color: 'var(--college-muted)' }}>
          {docs.length} file{docs.length === 1 ? '' : 's'} - {doneCount} done
        </p>
      </div>
      <div>
        {docs.map((doc) => (
          <DocRow key={doc.id} doc={doc} appNameById={appNameById} onToggleStatus={onToggleStatus} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

/* ─── Confirm dialog (fields only — no CSS-var/utility classes) ────────── */

function ConfirmDialog({ open, onOpenChange, form, setForm, apps, onSubmit, saving }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const meta = DOC_TYPE_META[form.docType] || DOC_TYPE_META.OTHER;

  const inputStyle = {
    width: '100%', border: `1px solid ${HEX.border}`, background: HEX.panel, color: HEX.ink,
    borderRadius: 4, padding: '8px 10px', fontSize: 13, fontFamily: SANS, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', marginBottom: 5, fontSize: 10, fontWeight: 600, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: HEX.muted2, fontFamily: SANS,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" style={{ background: HEX.panel, color: HEX.ink, border: `1px solid ${HEX.border}` }}>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: SERIF, fontStyle: 'italic', fontWeight: 400, fontSize: 24, color: HEX.ink }}>
            Link this file to your desk
          </DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 22, height: 22, borderRadius: 4, background: meta.color, color: '#fff', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, fontFamily: MONO,
              }}
            >
              {meta.letter}
            </div>
            <span style={{ fontSize: 11, color: HEX.muted, fontFamily: MONO }}>Detected as {meta.label}</span>
          </div>

          <div>
            <label style={labelStyle}>Name *</label>
            <input style={inputStyle} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Common App Personal Statement" />
          </div>

          <div>
            <label style={labelStyle}>URL *</label>
            <input style={inputStyle} value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://docs.google.com/…" />
          </div>

          <div>
            <label style={labelStyle}>Category</label>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {CATEGORY_QUICK_PICKS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => set('category', c)}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 999, cursor: 'pointer', fontFamily: SANS,
                    border: `1px solid ${form.category === c ? HEX.ink : HEX.border}`,
                    background: form.category === c ? HEX.ink : 'transparent',
                    color: form.category === c ? HEX.panel : HEX.inkSoft,
                  }}
                >
                  {c}
                </button>
              ))}
            </div>
            <input
              style={inputStyle}
              value={form.category}
              onChange={(e) => set('category', e.target.value)}
              placeholder="Or type your own…"
            />
          </div>

          <div>
            <label style={labelStyle}>Tag to a school</label>
            <select style={inputStyle} value={form.collegeApplicationId} onChange={(e) => set('collegeApplicationId', e.target.value)}>
              <option value="">— none, applies to all —</option>
              {apps.map((a) => (
                <option key={a.id} value={a.id}>{a.collegeName}</option>
              ))}
            </select>
          </div>
        </div>
        <DialogFooter style={{ marginTop: 12 }}>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            style={{ background: 'none', border: 'none', color: HEX.muted, fontSize: 13, cursor: 'pointer', fontFamily: SANS, padding: '8px 12px' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={saving}
            style={{
              background: HEX.ink, color: HEX.panel, border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600,
              cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1, fontFamily: SANS, padding: '9px 18px',
            }}
          >
            {saving ? 'Linking…' : 'Link file'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Page ───────────────────────────────────────────────────────────── */

export default function CollegeDocsPage() {
  const { api } = useAuth();
  const [docs, setDocs] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmForm, setConfirmForm] = useState(emptyConfirmForm());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get('/linked-documents'),
      api.get('/college-applications'),
    ])
      .then(([docsRes, appsRes]) => {
        if (!alive) return;
        setDocs(docsRes.data.documents || []);
        setApps(appsRes.data.applications || []);
      })
      .catch(() => toast.error('Failed to load documents'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const appNameById = useMemo(
    () => Object.fromEntries(apps.map((a) => [a.id, a.collegeName])),
    [apps]
  );

  const sections = useMemo(() => groupDocuments(docs), [docs]);

  /* ── Picker → confirm step ── */

  // Handles both the real Picker selection and GoogleDocsPicker's built-in
  // "paste link instead" fallback — the fallback payload has no mimeType, so
  // docTypeFromMime falls through to OTHER, which the user can leave as-is.
  const handleDocSelected = (payload) => {
    setConfirmForm({
      name: payload.title || 'Untitled document',
      url: payload.url || '',
      docType: docTypeFromMime(payload.mimeType),
      category: '',
      collegeApplicationId: '',
    });
    setConfirmOpen(true);
  };

  const handleConfirmSubmit = async () => {
    if (!confirmForm.name.trim()) return toast.error('Name is required');
    if (!confirmForm.url.trim()) return toast.error('URL is required');
    setSaving(true);
    try {
      const res = await api.post('/linked-documents', {
        name: confirmForm.name.trim(),
        url: confirmForm.url.trim(),
        docType: confirmForm.docType,
        category: confirmForm.category.trim() || null,
        owner: 'me',
        collegeApplicationId: confirmForm.collegeApplicationId || null,
      });
      setDocs((prev) => [res.data.document, ...prev]);
      toast.success(`Linked "${res.data.document.name}" to your desk`);
      setConfirmOpen(false);
      setConfirmForm(emptyConfirmForm());
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to link document');
    } finally {
      setSaving(false);
    }
  };

  /* ── mutations ── */

  const handleToggleStatus = async (doc) => {
    const nextStatus = doc.status === 'DONE' ? 'DRAFTING' : 'DONE';
    setDocs((prev) => prev.map((d) => (d.id === doc.id ? { ...d, status: nextStatus } : d)));
    try {
      const res = await api.put(`/linked-documents/${doc.id}`, { status: nextStatus });
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? res.data.document : d)));
    } catch {
      setDocs((prev) => prev.map((d) => (d.id === doc.id ? doc : d)));
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (doc) => {
    const snapshot = docs;
    setDocs((prev) => prev.filter((d) => d.id !== doc.id));
    try {
      await api.delete(`/linked-documents/${doc.id}`);
      toast.success(`Removed "${doc.name}"`);
    } catch {
      setDocs(snapshot);
      toast.error('Failed to remove document');
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap',
          gap: 24, borderBottom: '1px solid var(--college-border)', paddingBottom: 28, marginBottom: 36,
        }}
      >
        <div>
          <h1 className="font-serif-editorial" style={{ fontStyle: 'italic', fontWeight: 400, fontSize: 52, lineHeight: 1, margin: '0 0 10px' }}>
            Docs.
          </h1>
          <p className="font-mono-editorial" style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
            Every application file, pulled from Google Drive into one desk - {docs.length} file{docs.length === 1 ? '' : 's'} linked
          </p>
        </div>

        <GoogleDocsPicker
          onDocSelected={handleDocSelected}
          mimeTypes={PICKER_MIME_TYPES}
          buttonText="+ Pick from Drive"
          showIcon={false}
          className="bg-[#16130D] text-[#FFFDF8] hover:bg-[#2a251b] rounded-none border border-[#16130D] font-mono text-xs tracking-wider uppercase px-5 h-10"
        />
      </div>

      {/* Sections */}
      {loading ? (
        <div className="font-mono-editorial" style={{ padding: '48px 0', textAlign: 'center', color: 'var(--college-muted)', fontSize: 13 }}>
          Loading your desk…
        </div>
      ) : docs.length === 0 ? (
        <div style={{ padding: '64px 0', textAlign: 'center', border: '1px dashed var(--college-border)' }}>
          <p className="font-serif-editorial" style={{ fontStyle: 'italic', fontSize: 24, margin: '0 0 8px' }}>
            Nothing linked yet.
          </p>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--college-muted)' }}>
            Pick a file from Google Drive above to start tracking your application files here.
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 640 }}>
            {sections.map((section) => (
              <DocSection
                key={section.title}
                title={section.title}
                docs={section.docs}
                appNameById={appNameById}
                onToggleStatus={handleToggleStatus}
                onDelete={handleDelete}
              />
            ))}
          </div>
        </div>
      )}

      {/* Footer caption */}
      <p
        className="font-mono-editorial"
        style={{ marginTop: 48, fontSize: 11, color: 'var(--college-muted-2)', borderTop: '1px solid var(--college-border)', paddingTop: 16 }}
      >
        Files stay in your Drive - this desk only links them - Google Picker API + Drive API
      </p>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={(o) => { setConfirmOpen(o); if (!o) setConfirmForm(emptyConfirmForm()); }}
        form={confirmForm}
        setForm={setConfirmForm}
        apps={apps}
        onSubmit={handleConfirmSubmit}
        saving={saving}
      />
    </div>
  );
}
